import cx from 'classnames';
import {FormikProvider, useFormik} from 'formik';
import {includes, isEmpty, map, noop, orderBy} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {agentActionsApi} from 'api';
import {AssignmentUserSelectorFormik, ConfirmModal, DataWrapper} from 'components';
import {useApiData, useUserSession} from 'hooks';
import {Group, Option, ReassignDetails} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {UserSummary} from 'types';
import {userAuthUtils} from 'utils';
import {getUserName} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE;
const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;
const T_KEYS_ONLINE_STATE = TRANSLATION_KEYS.COMPONENTS.ONLINE_STATE;

type UserAvailabilityIconProps = {
    online?: boolean;
    offline?: boolean;
    inSession?: boolean;
    className?: string;
}

const UserAvailabilityIcon: React.FC<UserAvailabilityIconProps> = ({online, offline, inSession, className}) => (
    <div className={cx(
        'tw-flex-shrink-0 tw-inline-block tw-rounded-full tw-w-2.5 tw-h-2.5', className,
        {
            'tw-bg-success-light': online,
            'tw-bg-alert': offline,
            'tw-bg-primary-light': inSession,
        })}
    />
);

type Props = {
    open: boolean;
    isSipOrPhoneClient: boolean;
    selectedGroupId?: string;
    selectedSubgroupId?: string;
    groups?: Group[];
    onClose: (confirm: boolean, details: Partial<ReassignDetails>) => void;
}

export const ReassignModal: React.FC<Props> = ({
    open, isSipOrPhoneClient, selectedGroupId, selectedSubgroupId, groups, onClose,
}) => {
    const {t} = useTranslation();
    const [assignmentAvailabilityVisible, setAssignmentAvailabilityVisible] = useState(!isEmpty(groups));

    const {sessionParams} = useUserSession();
    const localUser = useMemo(() => userAuthUtils.getLocalUser(), []);
    const [lastAssignmentRequested, setLastAssignmentRequested] = useState<{
        groupId?: string;
        subgroupId?: string;
    }>(null);

    const {
        send: getAssignmentAvailability,
        data: assignmentAvailability,
        loading: assignmentAvailabilityLoading
    } = useApiData(agentActionsApi.getAssignmentAvailability);

    const {
        send: getUsersAvailability,
        data: usersAvailability,
        loading: usersAvailabilityLoading
    } = useApiData(agentActionsApi.getUsersAvailability);

    const loading = useMemo(() => assignmentAvailabilityLoading || usersAvailabilityLoading,
        [assignmentAvailabilityLoading, usersAvailabilityLoading]);

    const title = useMemo(() => t(T_KEYS.ACTIONS.REASSIGN), [t]);

    const initialValues = useMemo(() => ({
        assignment: {
            groupId: selectedGroupId,
            subgroupId: selectedSubgroupId
        },
        agent: {
            eid: null
        }
    }), [selectedGroupId, selectedSubgroupId]);

    const formik = useFormik({
        initialValues,
        onSubmit: noop
    });

    const {values, resetForm} = formik;

    const okDisabled = useMemo(() => loading || (!values.assignment && !values.agent.eid), [loading, values]);

    const onCloseModal = useCallback((confirm: boolean) => {
        onClose(confirm, values);
        resetForm();
    }, [onClose, values, resetForm]);

    const fetchAssignmentAvailability = useCallback((data: {groupId?: string, subgroupId?: string}) => {
        getAssignmentAvailability(sessionParams, data);
        setLastAssignmentRequested(data);
    }, [getAssignmentAvailability, sessionParams]);

    const updateAssignmentAvailability = useCallback((data: {groupId?: string, subgroupId?: string}) => {
        if (
            lastAssignmentRequested &&
            lastAssignmentRequested?.groupId === data.groupId &&
            lastAssignmentRequested?.subgroupId === data.subgroupId
        ) {
            return;
        }
        getAssignmentAvailability(sessionParams, data);
        setLastAssignmentRequested(data);
    }, [getAssignmentAvailability, sessionParams, lastAssignmentRequested]);

    const fetchUsersAvailability = useCallback(() => {
        getUsersAvailability(sessionParams);
    }, [getUsersAvailability, sessionParams]);

    // TODO: update AssignmentUserSelectorFormik component to avoid extra updates calls
    const handleAssignmentChange = useCallback((groupId: string, subgroupId: string) => {
        const isAssignmentTab = groupId !== null && !isEmpty(groups);
        setAssignmentAvailabilityVisible(isAssignmentTab);

        if (isAssignmentTab) {
            updateAssignmentAvailability({groupId, subgroupId});
        } else {
            setLastAssignmentRequested(null);
            fetchUsersAvailability();
        }
    }, [updateAssignmentAvailability, fetchUsersAvailability, groups]);

    const renderIcon = useCallback((eid: number) => (
        <UserAvailabilityIcon
            online={includes(usersAvailability?.onsiteUsersEids, eid)}
            inSession={includes(usersAvailability?.inSessionUsersEids, eid)}
            className='tw-mr-2'
        />
    ), [usersAvailability]);

    const orderedUsers = useMemo(() => orderBy(usersAvailability?.users, (user) => {
        if (localUser.eid === user.eid) {
            return 3;
        }
        if (includes(usersAvailability?.onsiteUsersEids, user.eid)) {
            return 2;
        }
        if (includes(usersAvailability?.inSessionUsersEids, user.eid)) {
            return 1;
        }
        return 0;
    }, 'desc'), [usersAvailability, localUser]);

    const renderOptions = useCallback((_users: UserSummary[]): Option<number, string>[] => map(_users, (user) => ({
        name: getUserName(t, user, user.email === localUser.email),
        id: user.eid,
        renderIcon
    })), [t, localUser, renderIcon]);

    useEffect(() => {
        if (open) {
            if (isEmpty(groups)) {
                fetchUsersAvailability();
            } else {
                fetchAssignmentAvailability({});
            }
        } else {
            setLastAssignmentRequested(null);
        }
    }, [open, fetchAssignmentAvailability, fetchUsersAvailability, groups]);

    if (isSipOrPhoneClient) {
        return (
            <ConfirmModal
                open={open}
                title={title}
                content={t(T_KEYS.MESSAGES.REASSIGN_TO_AI)}
                onClose={(confirm: boolean) => onClose(confirm, null)}
            />
        );
    }

    return (
        <FormikProvider value={formik}>
            <ConfirmModal
                open={open}
                title={title}
                maxWidth='sm'
                fullWidth
                okDisabled={okDisabled}
                onClose={onCloseModal}
            >
                <AssignmentUserSelectorFormik
                    assignmentSelectorProps={{
                        name: 'assignment',
                        groups,
                        disabled: loading,
                        fullWidth: true,
                        emptyValue: null,
                        onChange: handleAssignmentChange
                    }}
                    userSelectorProps={{
                        name: 'agent.eid',
                        context: 'agent',
                        field: 'eid',
                        users: orderedUsers,
                        renderOptions
                    }}
                />
                <DataWrapper isLoading={loading}>
                    {assignmentAvailability && assignmentAvailabilityVisible &&
                        <div>
                            <div className='tw-ts-h5 tw-mt-6 tw-mb-2'>
                                {t(T_KEYS.LABELS.AVAILABILITY)}
                            </div>
                            <div className='tw-flex tw-items-center'>
                                <UserAvailabilityIcon
                                    online={assignmentAvailability.online > 0}
                                    offline={assignmentAvailability.online === 0}
                                    className='tw-mr-1.5'
                                />
                                <span className='tw-px-1'>
                                    {`${t(T_KEYS_ONLINE_STATE.ONLINE)}: `}
                                </span>
                                {assignmentAvailability.online || 0}
                                <span className='tw-px-1'>{t(T_KEYS.LABELS.OF)}</span>
                                {assignmentAvailability.total || 0}
                                <UserAvailabilityIcon inSession className='tw-ml-6 tw-mr-1.5' />
                                <span className='tw-px-1'>{`${t(T_KEYS_COMMON.ALL.IN_SESSION)}: `}</span>
                                {assignmentAvailability.inSession || 0}
                            </div>
                        </div>}
                </DataWrapper>
            </ConfirmModal>
        </FormikProvider>
    );
};